import React, { useState, useEffect, useRef } from 'react';
import { Monitor, Smartphone, Plus, ArrowLeft, Clock, CheckCircle, Trash2, RotateCcw, Play, SkipForward, Users, Image as ImageIcon, Pencil, Trophy } from 'lucide-react';
import { db } from './firebase';
import firebase from 'firebase/compat/app';

export default function App() {
  const [view, setView] = useState('home');
  const [adminPIN] = useState('123456');
  const [inputPIN, setInputPIN] = useState('');

  const [quizzes, setQuizzes] = useState({});
  const [selectedQuizCode, setSelectedQuizCode] = useState(null);
  const [liveQuizData, setLiveQuizData] = useState(null);
  const [showLeaderboardStep, setShowLeaderboardStep] = useState(false);

  const [studentName, setStudentName] = useState('');
  const [quizCode, setQuizCode] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [hasAnsweredCurrent, setHasAnsweredCurrent] = useState(false);
  const [myScore, setMyScore] = useState(0);

  const [formTitle, setFormTitle] = useState('');
  const [formQuestions, setFormQuestions] = useState([
    { question: '', image: '', options: ['', '', '', ''], correct: 0, timeLimit: 20 }
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [editingCode, setEditingCode] = useState(null);

  const [nowTick, setNowTick] = useState(Date.now());
  const [serverTimeOffset, setServerTimeOffset] = useState(0);
  const revealTriggeredRef = useRef(null);

  const optionLabels = ['A', 'B', 'C', 'D'];
  const MAX_POINTS_PER_SOAL = 25; // poin untuk jawaban benar TERCEPAT di antara peserta
  const MIN_POINTS_PER_SOAL = 15; // poin untuk jawaban benar TERLAMBAT di antara peserta (tetap dihargai)

  // 1. Dengar Seluruh Kuis dari Firebase Realtime
  useEffect(() => {
    const quizzesRef = db.ref('quizzes');
    const handleValue = (snapshot) => {
      const data = snapshot.val();
      setQuizzes(data || {});
    };
    quizzesRef.on('value', handleValue);
    return () => quizzesRef.off('value', handleValue);
  }, []);

  // 2. Sync Realtime Kuis Aktif
  useEffect(() => {
    if (!selectedQuizCode) return;
    const activeRef = db.ref(`quizzes/${selectedQuizCode}`);
    const handleLive = (snapshot) => {
      const data = snapshot.val();
      setLiveQuizData(data);

      if (data) {
        if (view === 'student-waiting' && data.status === 'started') {
          setView('student-quiz');
        } else if (data.status === 'finished' && view === 'student-quiz') {
          setView('result');
        }
      }
    };
    activeRef.on('value', handleLive);
    return () => activeRef.off('value', handleLive);
  }, [selectedQuizCode, view]);

  useEffect(() => {
    setSelectedOption(null);
    setHasAnsweredCurrent(false);
    setShowLeaderboardStep(false);
  }, [liveQuizData?.currentQuestion, selectedQuizCode]);

  // 3. Tick lokal untuk countdown, + offset waktu server (agar countdown akurat walau jam device beda-beda)
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const offsetRef = db.ref('.info/serverTimeOffset');
    const handleOffset = (snap) => setServerTimeOffset(snap.val() || 0);
    offsetRef.on('value', handleOffset);
    return () => offsetRef.off('value', handleOffset);
  }, []);

  const currentQForTimer = liveQuizData?.questions?.[liveQuizData.currentQuestion];
  const timeLimitMs = ((currentQForTimer && currentQForTimer.timeLimit) || 20) * 1000;
  const serverNow = nowTick + serverTimeOffset;
  const elapsedMs = liveQuizData?.questionStartedAt ? serverNow - liveQuizData.questionStartedAt : 0;
  const remainingMs = liveQuizData?.questionStartedAt ? Math.max(0, timeLimitMs - elapsedMs) : timeLimitMs;
  const remainingSec = Math.ceil(remainingMs / 1000);

  // Hitung poin per soal berdasarkan kecepatan RELATIF antar peserta (bukan dibandingkan batas waktu soal),
  // supaya tidak terpengaruh jam device yang tidak sinkron. Patokannya: answeredAt & questionStartedAt
  // sama-sama timestamp SERVER Firebase, jadi konsisten untuk semua orang.
  // - Yang benar & paling cepat menjawab -> MAX_POINTS_PER_SOAL
  // - Yang benar & paling lambat menjawab (di antara yang benar) -> MIN_POINTS_PER_SOAL
  // - Yang benar di antara keduanya -> diskalakan linear
  // - Yang salah / tidak menjawab -> 0
  const applyQuestionScores = (questionIndex) => {
    const questionAnswers = (liveQuizData.answers && liveQuizData.answers[questionIndex]) || {};
    const entries = Object.entries(questionAnswers);
    if (entries.length === 0) {
      // Tidak ada yang menjawab soal ini - tetap tandai selesai supaya tombol tidak macet menunggu.
      db.ref(`quizzes/${selectedQuizCode}/scoreReady/${questionIndex}`).set(true);
      return;
    }

    const startedAt = liveQuizData.questionStartedAt || 0;
    let fastestElapsed = null;
    let slowestElapsed = null;
    entries.forEach(([, a]) => {
      if (!a || !a.correct || typeof a.answeredAt !== 'number') return;
      const elapsed = Math.max(0, a.answeredAt - startedAt);
      if (fastestElapsed === null || elapsed < fastestElapsed) fastestElapsed = elapsed;
      if (slowestElapsed === null || elapsed > slowestElapsed) slowestElapsed = elapsed;
    });

    const updates = {};
    entries.forEach(([studentKey, a]) => {
      if (!a) return;
      let points = 0;
      if (a.correct) {
        if (fastestElapsed === null || slowestElapsed === fastestElapsed) {
          points = MAX_POINTS_PER_SOAL;
        } else {
          const elapsed = Math.max(0, (typeof a.answeredAt === 'number' ? a.answeredAt : startedAt) - startedAt);
          const ratio = (slowestElapsed - elapsed) / (slowestElapsed - fastestElapsed);
          points = Math.round(MIN_POINTS_PER_SOAL + (MAX_POINTS_PER_SOAL - MIN_POINTS_PER_SOAL) * ratio);
        }
      }
      const prevScore = (liveQuizData.results && liveQuizData.results[studentKey] && liveQuizData.results[studentKey].score) || 0;
      updates[`answers/${questionIndex}/${studentKey}/points`] = points;
      updates[`results/${studentKey}/score`] = prevScore + points;
      updates[`results/${studentKey}/lastPoints`] = points;
      updates[`results/${studentKey}/name`] = a.name;
      updates[`results/${studentKey}/total`] = liveQuizData.questions.length;
      updates[`results/${studentKey}/lastAnswer`] = optionLabels[a.optionIdx] !== undefined ? optionLabels[a.optionIdx] : '-';
      updates[`results/${studentKey}/updatedAt`] = new Date().toLocaleTimeString('id-ID');
    });

    // Tandai skor soal ini SUDAH LENGKAP tersimpan, dalam update yang SAMA dengan angka skornya
    // (jadi begitu flag ini true, angka skornya PASTI sudah ikut tersimpan juga - tidak ada jeda/race).
    updates[`scoreReady/${questionIndex}`] = true;

    if (Object.keys(updates).length > 0) {
      db.ref(`quizzes/${selectedQuizCode}`).update(updates);
    }
  };

  // 4. Saat waktu jawab habis, otomatis buka kunci jawaban + hitung skor
  // (dipicu dari device manapun yang masih membuka kuis ini, supaya tidak bergantung 1 layar)
  useEffect(() => {
    if (!liveQuizData || liveQuizData.status !== 'started' || !selectedQuizCode) return;
    if (liveQuizData.revealed) {
      revealTriggeredRef.current = null;
      return;
    }
    if (!liveQuizData.questionStartedAt) return;
    if (remainingMs <= 0 && revealTriggeredRef.current !== liveQuizData.currentQuestion) {
      revealTriggeredRef.current = liveQuizData.currentQuestion;
      const questionIndex = liveQuizData.currentQuestion;
      db.ref(`quizzes/${selectedQuizCode}`).update({ revealed: true });
      // Klaim hak hitung skor lewat transaction, supaya kalau banyak device mendeteksi waktu habis
      // secara bersamaan, skor tetap dihitung & ditambahkan HANYA SEKALI (tidak dobel).
      db.ref(`quizzes/${selectedQuizCode}/scoredQuestions/${questionIndex}`).transaction(
        (current) => (current ? undefined : true),
        (error, committed) => {
          if (!error && committed) {
            applyQuestionScores(questionIndex);
          }
        }
      );
    }
  }, [remainingMs, liveQuizData, selectedQuizCode]);

  const currentAnswers = (liveQuizData?.answers && liveQuizData.answers[liveQuizData.currentQuestion]) || {};
  const answerCounts = [0, 0, 0, 0];
  Object.values(currentAnswers).forEach((a) => {
    if (a && typeof a.optionIdx === 'number' && a.optionIdx >= 0 && a.optionIdx < 4) {
      answerCounts[a.optionIdx] += 1;
    }
  });

  const participantCount = liveQuizData?.participants ? Object.keys(liveQuizData.participants).length : 0;
  const participantNames = liveQuizData?.participants ? Object.values(liveQuizData.participants).map((p) => p.name) : [];

  const sortedResults = liveQuizData?.results
    ? Object.values(liveQuizData.results).sort((a, b) => (b.score || 0) - (a.score || 0))
    : [];

  const isLastQuestion = liveQuizData ? liveQuizData.currentQuestion + 1 >= liveQuizData.questions.length : false;

  const myStudentKey = studentName ? studentName.replace(/[^a-zA-Z0-9]/g, '_') : '';
  const myLastPoints = liveQuizData?.results?.[myStudentKey]?.lastPoints;

  // Skor soal aktif dianggap "siap" kalau sudah ditandai scoreReady (dihitung barengan angkanya, jadi tidak ada race).
  // Jaga-jaga kalau flag itu gagal terkirim (mis. gangguan jaringan), izinkan lanjut setelah beberapa detik grace period.
  const scoreReadyForCurrent = !!(liveQuizData?.scoreReady && liveQuizData.scoreReady[liveQuizData?.currentQuestion]);
  const msPastDeadline = liveQuizData?.questionStartedAt ? serverNow - (liveQuizData.questionStartedAt + timeLimitMs) : 0;
  const scoreGraceOk = msPastDeadline > 4000;
  const canProceedAfterReveal = !!liveQuizData?.revealed && (scoreReadyForCurrent || scoreGraceOk);

  // Soal hanya tampil saat pratinjau (belum mulai) atau saat aktif & belum masuk tahap "lihat klasemen"
  const showQuestionBox = liveQuizData && (liveQuizData.status === 'waiting' || (liveQuizData.status === 'started' && !showLeaderboardStep));
  // Klasemen hanya tampil di tahap "lihat klasemen" antar-soal, atau saat kuis sudah selesai (klasemen akhir)
  const showLeaderboardBox = liveQuizData && (liveQuizData.status === 'finished' || (liveQuizData.status === 'started' && showLeaderboardStep));

  const handleControlButtonClick = () => {
    if (!liveQuizData || !canProceedAfterReveal) return;
    if (isLastQuestion) {
      // Soal terakhir: langsung akhiri kuis & tampilkan Klasemen Akhir, tanpa tahap klasemen sementara
      handleNextQuestion();
      return;
    }
    if (!showLeaderboardStep) {
      setShowLeaderboardStep(true);
    } else {
      setShowLeaderboardStep(false);
      handleNextQuestion();
    }
  };

  const handleAdminLogin = (e) => {
    e.preventDefault();
    if (inputPIN === adminPIN) {
      setView('admin-dashboard');
      setInputPIN('');
    } else {
      alert('Kode Akses Admin Salah!');
    }
  };

  // PROSES SIMPAN KUIS REAL (DIRECT TRIGGER) - berlaku untuk buat baru maupun edit
  const executeSaveQuiz = () => {
    if (!formTitle || !formTitle.trim()) {
      alert('Mohon isi Judul Kuis terlebih dahulu!');
      return;
    }

    setIsSaving(true);

    const sanitizedQuestions = formQuestions.map((q) => ({
      question: q.question.trim() || 'Pertanyaan',
      image: q.image || '',
      options: q.options.map((opt) => opt.trim() || '-'),
      correct: Number(q.correct) || 0,
      timeLimit: Number(q.timeLimit) > 0 ? Number(q.timeLimit) : 20
    }));

    // MODE EDIT: update soal kuis yang sudah ada, tanpa mengubah kode/status/hasil yang sudah berjalan
    if (editingCode) {
      db.ref('quizzes/' + editingCode).update(
        { title: formTitle.trim(), questions: sanitizedQuestions },
        (error) => {
          setIsSaving(false);
          if (error) {
            console.error("Firebase Error:", error);
            alert("Gagal menyimpan perubahan! Keterangan Eror: " + error.message);
          } else {
            alert('Perubahan kuis berhasil disimpan!');
            setEditingCode(null);
            setFormTitle('');
            setFormQuestions([{ question: '', image: '', options: ['', '', '', ''], correct: 0, timeLimit: 20 }]);
            setView('admin-dashboard');
          }
        }
      );
      return;
    }

    // MODE BARU: buat kuis dengan kode 6 digit baru
    const code = Math.floor(100000 + Math.random() * 900000).toString();

    const quizPayload = {
      code: code,
      title: formTitle.trim(),
      status: 'waiting',
      currentQuestion: 0,
      questions: sanitizedQuestions,
      createdAt: new Date().toISOString()
    };

    // PENTING: Gunakan callback langsung (bukan Promise/Await) agar tidak mengantung
    db.ref('quizzes/' + code).set(quizPayload, (error) => {
      setIsSaving(false);

      if (error) {
        console.error("Firebase Error:", error);
        alert("Gagal menyimpan! Keterangan Eror: " + error.message);
      } else {
        alert(`BERHASIL DISIMPAN!\n\nKode Kuis: ${code}`);
        setFormTitle('');
        setFormQuestions([{ question: '', image: '', options: ['', '', '', ''], correct: 0, timeLimit: 20 }]);
        setView('admin-dashboard');
      }
    });
  };

  const handleEditQuiz = (quiz) => {
    setEditingCode(quiz.code);
    setFormTitle(quiz.title || '');
    setFormQuestions(
      (quiz.questions && quiz.questions.length ? quiz.questions : [{ question: '', image: '', options: ['', '', '', ''], correct: 0, timeLimit: 20 }])
        .map((q) => ({
          question: q.question || '',
          image: q.image || '',
          options: q.options ? [...q.options] : ['', '', '', ''],
          correct: q.correct || 0,
          timeLimit: q.timeLimit || 20
        }))
    );
    setView('admin-form');
  };

  const handleStartQuiz = () => {
    db.ref(`quizzes/${selectedQuizCode}`).update({
      status: 'started',
      currentQuestion: 0,
      questionStartedAt: firebase.database.ServerValue.TIMESTAMP,
      revealed: false
    });
  };

  const handleNextQuestion = () => {
    if (liveQuizData.currentQuestion + 1 < liveQuizData.questions.length) {
      db.ref(`quizzes/${selectedQuizCode}`).update({
        currentQuestion: liveQuizData.currentQuestion + 1,
        questionStartedAt: firebase.database.ServerValue.TIMESTAMP,
        revealed: false
      });
    } else {
      db.ref(`quizzes/${selectedQuizCode}`).update({
        status: 'finished'
      });
    }
  };

  const handleResetQuiz = (code) => {
    if (window.confirm('Reset kuis ini? Semua jawaban & peserta yang tergabung akan dihapus.')) {
      db.ref(`quizzes/${code}`).update({
        status: 'waiting',
        currentQuestion: 0,
        results: null,
        participants: null,
        answers: null,
        revealed: false,
        questionStartedAt: null,
        scoredQuestions: null,
        scoreReady: null
      });
    }
  };

  const handleDeleteQuiz = (code) => {
    if (window.confirm('Hapus kuis ini dari database?')) {
      db.ref(`quizzes/${code}`).remove();
    }
  };

  const handleStudentJoin = (e) => {
    e.preventDefault();
    const targetQuiz = quizzes[quizCode];
    if (!targetQuiz) {
      alert('Kode kuis 6 digit tidak ditemukan di database!');
      return;
    }
    setSelectedQuizCode(quizCode);

    const studentKey = studentName.replace(/[^a-zA-Z0-9]/g, '_');
    db.ref(`quizzes/${quizCode}/participants/${studentKey}`).set({
      name: studentName,
      joinedAt: firebase.database.ServerValue.TIMESTAMP
    });

    if (targetQuiz.status === 'started') {
      setView('student-quiz');
    } else {
      setView('student-waiting');
    }
  };

  const handleStudentAnswer = (optIdx) => {
    // Boleh diklik berkali-kali (ganti jawaban) selama waktu belum habis / belum revealed.
    if (liveQuizData.revealed) return;
    setSelectedOption(optIdx);
    setHasAnsweredCurrent(true);

    const questionIndex = liveQuizData.currentQuestion;
    const questionObj = liveQuizData.questions[questionIndex];
    const isCorrect = optIdx === questionObj.correct;
    const studentKey = studentName.replace(/[^a-zA-Z0-9]/g, '_');

    // Simpan jawaban mentah saja di sini (waktu jawab pakai timestamp SERVER Firebase, bukan jam device).
    // .set() menimpa jawaban sebelumnya kalau mahasiswa mengganti pilihan - answeredAt ikut diperbarui
    // ke waktu jawaban TERAKHIR mereka, jadi kecepatan yang dinilai adalah kecepatan mengunci jawaban final.
    // Poinnya dihitung belakangan saat waktu habis (lihat applyQuestionScores), dibandingkan RELATIF
    // terhadap kecepatan peserta lain, bukan terhadap batas waktu soal.
    db.ref(`quizzes/${selectedQuizCode}/answers/${questionIndex}/${studentKey}`).set({
      name: studentName,
      optionIdx: optIdx,
      correct: isCorrect,
      answeredAt: firebase.database.ServerValue.TIMESTAMP
    });
  };

  const handleImageUpload = (e, qIndex) => {
    const file = e.target.files[0];
    if (file) {
      // Kompres / batasi ukuran jika perlu
      const reader = new FileReader();
      reader.onloadend = () => {
        const updated = [...formQuestions];
        updated[qIndex].image = reader.result;
        setFormQuestions(updated);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetToHome = () => {
    setStudentName('');
    setQuizCode('');
    setSelectedQuizCode(null);
    setLiveQuizData(null);
    setView('home');
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4 font-sans">
      
      {/* 1. HOME */}
      {view === 'home' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
          <h1 className="text-3xl font-black text-slate-800 mb-1">KUIS INTERAKTIF</h1>
          <p className="text-slate-500 font-semibold mb-8">Teknik Elektro UPGRIS</p>
          <div className="space-y-4">
            <button onClick={() => setView('admin-login')} className="w-full flex items-center justify-center gap-3 bg-indigo-600 text-white font-semibold py-3 rounded-xl shadow-md hover:bg-indigo-700">
              <Monitor size={20} /> Masuk Layar Dosen
            </button>
            <button onClick={() => setView('student-login')} className="w-full flex items-center justify-center gap-3 bg-emerald-600 text-white font-semibold py-3 rounded-xl shadow-md hover:bg-emerald-700">
              <Smartphone size={20} /> Masuk Sebagai Mahasiswa
            </button>
          </div>
        </div>
      )}

      {/* LOGIN MAHASISWA */}
      {view === 'student-login' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
          <button onClick={() => setView('home')} className="flex items-center gap-2 text-slate-500 mb-6"><ArrowLeft size={18} /> Kembali</button>
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Gabung Kuis Mahasiswa</h2>
          <form onSubmit={handleStudentJoin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nama / Kelompok</label>
              <input type="text" required placeholder="Contoh: Kelompok 1 Elektro" value={studentName} onChange={(e) => setStudentName(e.target.value)} className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Kode Kuis (6 Digit)</label>
              <input type="text" maxLength={6} required placeholder="Masukkan 6 digit angka" value={quizCode} onChange={(e) => setQuizCode(e.target.value)} className="w-full px-4 py-2 border rounded-lg font-mono text-center text-lg tracking-widest" />
            </div>
            <button type="submit" className="w-full bg-emerald-600 text-white font-semibold py-3 rounded-xl hover:bg-emerald-700">Masuk Ruang Tunggu</button>
          </form>
        </div>
      )}

      {/* LAYAR TUNGGU MAHASISWA */}
      {view === 'student-waiting' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
          <Clock className="mx-auto text-amber-500 animate-spin mb-4" size={48} />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Menunggu Dosen Memulai Kuis...</h2>
          <p className="text-slate-500 text-sm mb-4">Harap bersiap, kuis akan otomatis dimulai di layar Anda saat Dosen klik Start.</p>
          <span className="inline-block bg-slate-100 text-slate-700 font-semibold px-4 py-2 rounded-full text-xs">
            Mahasiswa: {studentName}
          </span>
        </div>
      )}

      {/* LAYAR PENGERJAAN MAHASISWA - hanya pilihan jawaban + countdown, soal ada di layar Dosen */}
      {view === 'student-quiz' && liveQuizData && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-xl w-full">
          <div className="flex justify-between items-center mb-4">
            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">{studentName}</span>
            <span className="text-xs font-bold text-slate-500">Soal {liveQuizData.currentQuestion + 1} dari {liveQuizData.questions.length}</span>
          </div>

          <div className="text-center mb-6">
            <div className={`inline-flex items-center gap-2 px-5 py-2 rounded-full font-black text-lg ${
              liveQuizData.revealed ? 'bg-slate-200 text-slate-600' : remainingSec <= 5 ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'
            }`}>
              <Clock size={20} />
              {liveQuizData.revealed ? 'Waktu Habis' : `${remainingSec} detik`}
            </div>
            <p className="text-slate-400 text-xs mt-2">Lihat soalnya di layar Dosen, lalu pilih jawaban Anda di bawah ini.</p>
          </div>

          <div className="space-y-3 mb-6">
            {liveQuizData.questions[liveQuizData.currentQuestion]?.options.map((opt, idx) => {
              const isCorrectOpt = idx === liveQuizData.questions[liveQuizData.currentQuestion].correct;
              // Tidak ada warna/tanda yang menunjukkan pilihan mahasiswa sendiri - hanya jawaban benar yang disorot setelah waktu habis.
              // Sebelum waktu habis, tombol tetap bisa diklik berkali-kali untuk ganti jawaban.
              const styleClass = liveQuizData.revealed
                ? (isCorrectOpt ? 'border-emerald-600 bg-emerald-50 font-bold text-emerald-900' : 'border-slate-200 opacity-60')
                : 'border-slate-200 hover:bg-slate-50 active:scale-95';
              return (
                <button
                  key={idx}
                  disabled={liveQuizData.revealed}
                  onClick={() => handleStudentAnswer(idx)}
                  className={`w-full text-left p-4 rounded-xl border transition flex items-center gap-3 ${styleClass}`}
                >
                  <span className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center font-bold text-xs">{optionLabels[idx]}</span>
                  <span>{opt}</span>
                </button>
              );
            })}
          </div>

          {hasAnsweredCurrent && !liveQuizData.revealed && (
            <div className="text-center p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-sm font-semibold">
              Jawaban terkirim ke Dosen! Anda masih bisa mengganti pilihan selama waktu belum habis.
            </div>
          )}

          {liveQuizData.revealed && (
            <div className={`text-center p-3 rounded-xl text-sm font-semibold border ${
              hasAnsweredCurrent && selectedOption === liveQuizData.questions[liveQuizData.currentQuestion].correct
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-rose-50 border-rose-200 text-rose-800'
            }`}>
              {hasAnsweredCurrent
                ? (selectedOption === liveQuizData.questions[liveQuizData.currentQuestion].correct
                    ? (scoreReadyForCurrent ? `Jawaban Anda Benar! +${myLastPoints ?? 0} poin` : 'Jawaban Anda Benar! Menghitung poin...')
                    : 'Jawaban Anda Salah. +0 poin')
                : 'Anda tidak sempat menjawab soal ini.'}
              {' '}Jawaban benar: {optionLabels[liveQuizData.questions[liveQuizData.currentQuestion].correct]}
            </div>
          )}
        </div>
      )}

      {/* LOGIN DOSEN */}
      {view === 'admin-login' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
          <button onClick={() => setView('home')} className="flex items-center gap-2 text-slate-500 mb-6"><ArrowLeft size={18} /> Kembali</button>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Akses Dosen</h2>
          <form onSubmit={handleAdminLogin} className="space-y-4">
            <input type="password" required placeholder="Masukkan PIN Dosen" value={inputPIN} onChange={(e) => setInputPIN(e.target.value)} className="w-full px-4 py-2 border rounded-lg text-center" />
            <button type="submit" className="w-full bg-indigo-600 text-white font-semibold py-3 rounded-xl hover:bg-indigo-700">Masuk Dashboard</button>
          </form>
        </div>
      )}

      {/* DASHBOARD DOSEN */}
      {view === 'admin-dashboard' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-4xl w-full">
          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">Dashboard Dosen</h2>
              <p className="text-slate-500 text-sm">Teknik Elektro UPGRIS</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setEditingCode(null); setFormTitle(''); setFormQuestions([{ question: '', image: '', options: ['', '', '', ''], correct: 0, timeLimit: 20 }]); setView('admin-form'); }} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-indigo-700">
                <Plus size={18} /> Buat Kuis Baru
              </button>
              <button onClick={resetToHome} className="border px-3 py-2 rounded-xl text-sm hover:bg-slate-50">Keluar</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.keys(quizzes).length === 0 ? (
              <p className="text-slate-400 text-center col-span-2 py-8">Belum ada kuis di database Firebase.</p>
            ) : (
              Object.values(quizzes).map((quiz) => (
                <div key={quiz.code} className="border p-5 rounded-2xl bg-slate-50 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="bg-indigo-100 text-indigo-700 font-mono font-bold text-xs px-2.5 py-1 rounded">Kode: {quiz.code}</span>
                      <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                        quiz.status === 'started' ? 'bg-emerald-100 text-emerald-700' : quiz.status === 'finished' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {quiz.status === 'started' ? 'BERJALAN' : quiz.status === 'finished' ? 'SELESAI' : 'DRAFT'}
                      </span>
                    </div>
                    <h4 className="font-bold text-slate-800 text-lg mb-4">{quiz.title}</h4>
                  </div>
                  <div className="flex items-center gap-2 border-t pt-3">
                    {quiz.status === 'finished' ? (
                      <button onClick={() => { setSelectedQuizCode(quiz.code); setView('admin-control'); }} className="flex-1 bg-amber-500 text-white text-xs font-semibold py-2 rounded-lg flex items-center justify-center gap-1 hover:bg-amber-600">
                        <Trophy size={14} /> Lihat Klasemen Akhir
                      </button>
                    ) : (
                      <button onClick={() => { setSelectedQuizCode(quiz.code); setView('admin-control'); }} className="flex-1 bg-indigo-600 text-white text-xs font-semibold py-2 rounded-lg flex items-center justify-center gap-1 hover:bg-indigo-700">
                        <Monitor size={14} /> Kontrol Kuis
                      </button>
                    )}
                    <button onClick={() => handleEditQuiz(quiz)} className="bg-amber-100 text-amber-800 p-2 rounded-lg hover:bg-amber-200" title="Edit Kuis"><Pencil size={16} /></button>
                    <button onClick={() => handleResetQuiz(quiz.code)} className="bg-blue-100 text-blue-800 p-2 rounded-lg hover:bg-blue-200" title="Reset Hasil"><RotateCcw size={16} /></button>
                    <button onClick={() => handleDeleteQuiz(quiz.code)} className="bg-rose-100 text-rose-800 p-2 rounded-lg hover:bg-rose-200" title="Hapus Kuis"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* RUANG KONTROL DOSEN */}
      {view === 'admin-control' && liveQuizData && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-4xl w-full">
          <button onClick={() => setView('admin-dashboard')} className="flex items-center gap-2 text-slate-500 mb-4 text-sm"><ArrowLeft size={16} /> Kembali</button>

          <div className="flex flex-wrap justify-between items-center border-b pb-4 mb-6 gap-3">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">{liveQuizData.title}</h2>
              <p className="text-slate-500 font-mono text-sm">Kode Kuis Mahasiswa: <span className="font-bold text-indigo-600">{liveQuizData.code}</span></p>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1.5 text-xs font-bold text-slate-600 bg-slate-100 px-3 py-2 rounded-full" title={participantNames.join(', ') || 'Belum ada peserta'}>
                <Users size={14} /> {participantCount} Peserta Bergabung
              </span>
              {liveQuizData.status === 'started' && (
                <button
                  onClick={handleControlButtonClick}
                  disabled={!canProceedAfterReveal}
                  className={`flex items-center gap-2 font-bold px-4 py-2 rounded-xl text-sm ${
                    !canProceedAfterReveal ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700 text-white'
                  }`}
                >
                  <SkipForward size={18} />
                  {!liveQuizData.revealed
                    ? 'Menunggu Waktu Habis...'
                    : !canProceedAfterReveal
                      ? 'Menghitung Skor...'
                      : isLastQuestion
                        ? 'Akhiri & Lihat Klasemen Akhir'
                        : (!showLeaderboardStep ? 'Lihat Klasemen' : 'Lanjut Soal Berikutnya')}
                </button>
              )}
              {liveQuizData.status === 'waiting' && (
                <button onClick={handleStartQuiz} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-xl text-sm">
                  <Play size={18} /> Mulai Kuis Sekarang
                </button>
              )}
              {liveQuizData.status === 'finished' && (
                <>
                  <span className="flex items-center gap-2 bg-amber-100 text-amber-800 font-bold px-4 py-2 rounded-xl text-sm">
                    <Trophy size={18} /> Kuis Telah Selesai
                  </span>
                  <button onClick={() => handleResetQuiz(liveQuizData.code)} className="flex items-center gap-2 border border-slate-300 text-slate-600 font-semibold px-4 py-2 rounded-xl text-sm hover:bg-slate-50">
                    <RotateCcw size={16} /> Mulai Ulang (Reset)
                  </button>
                </>
              )}
            </div>
          </div>

          {liveQuizData.status !== 'started' && participantCount > 0 && (
            <div className="mb-6 -mt-2 flex flex-wrap gap-1.5">
              {participantNames.map((name, i) => (
                <span key={i} className="text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-full">{name}</span>
              ))}
            </div>
          )}

          {showQuestionBox && (
          <div className="bg-slate-50 p-6 rounded-2xl border mb-6">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold text-indigo-600 uppercase">
                {liveQuizData.status === 'waiting' ? 'Pratinjau Soal Pertama' : `Soal Aktif Dosen (Nomor ${liveQuizData.currentQuestion + 1} dari ${liveQuizData.questions.length})`}
              </span>
              {liveQuizData.status === 'started' && (
                <span className={`flex items-center gap-1 text-sm font-black px-3 py-1 rounded-full ${
                  liveQuizData.revealed ? 'bg-slate-200 text-slate-600' : remainingSec <= 5 ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'
                }`}>
                  <Clock size={14} /> {liveQuizData.revealed ? 'Waktu Habis' : `${remainingSec}s`}
                </span>
              )}
            </div>
            <h3 className="text-lg font-bold text-slate-800 mt-1 mb-3">{liveQuizData.questions[liveQuizData.currentQuestion]?.question}</h3>
            {liveQuizData.questions[liveQuizData.currentQuestion]?.image && (
              <img src={liveQuizData.questions[liveQuizData.currentQuestion].image} alt="Soal" className="max-h-40 rounded-lg mb-4 border" />
            )}
            <div className="grid grid-cols-2 gap-2">
              {liveQuizData.questions[liveQuizData.currentQuestion]?.options.map((opt, idx) => {
                const isCorrectOpt = idx === liveQuizData.questions[liveQuizData.currentQuestion].correct;
                const showAnswer = liveQuizData.status === 'waiting' || liveQuizData.revealed;
                return (
                  <div key={idx} className={`flex items-center justify-between p-2.5 rounded-lg border text-sm font-medium ${showAnswer && isCorrectOpt ? 'bg-emerald-100 border-emerald-400 text-emerald-900 font-bold' : 'bg-white'}`}>
                    <span>{optionLabels[idx]}. {opt}</span>
                    {liveQuizData.revealed && <span className="text-xs font-bold text-slate-500 ml-2 whitespace-nowrap">{answerCounts[idx]} suara</span>}
                  </div>
                );
              })}
            </div>
            {liveQuizData.status === 'started' && !liveQuizData.revealed && (
              <p className="text-xs text-slate-400 mt-3">Kunci jawaban & statistik akan tampil otomatis saat waktu habis.</p>
            )}
            {liveQuizData.revealed && (
              <div className="mt-3 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                Jawaban benar: {optionLabels[liveQuizData.questions[liveQuizData.currentQuestion].correct]}. Klik tombol di atas untuk lanjut.
              </div>
            )}
          </div>
          )}

          {showLeaderboardBox && (
          <div>
            <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Trophy size={18} /> {liveQuizData.status === 'finished' ? 'Klasemen Akhir' : 'Update Klasemen Sementara'}
            </h3>
            {sortedResults.length === 0 ? (
              <p className="text-slate-400 text-sm italic bg-slate-50 p-4 rounded-xl border text-center">Belum ada respon dari mahasiswa.</p>
            ) : (
              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-100 text-slate-700 font-bold border-b">
                    <tr>
                      <th className="p-3">#</th>
                      <th className="p-3">Nama Mahasiswa / Tim</th>
                      <th className="p-3 text-right">Skor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedResults.map((res, idx) => (
                      <tr key={idx} className={`border-b ${idx === 0 ? 'bg-amber-50' : ''}`}>
                        <td className="p-3 font-bold text-slate-500">{idx + 1}</td>
                        <td className="p-3 font-semibold text-slate-800">{idx === 0 ? '🏆 ' : ''}{res.name}</td>
                        <td className="p-3 text-right font-bold text-emerald-600">{res.score} poin</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}
        </div>
      )}

      {/* FORM BUAT KUIS - BEBAS DARI WRAPPER FORM INTERCEPT */}
      {view === 'admin-form' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-2xl w-full">
          <button onClick={() => { setEditingCode(null); setView('admin-dashboard'); }} type="button" className="flex items-center gap-2 text-slate-500 mb-4 text-sm">
            <ArrowLeft size={16} /> Batal
          </button>
          <h2 className="text-2xl font-bold text-slate-800 mb-6">{editingCode ? 'Edit Kuis' : 'Buat Kuis Baru'}</h2>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-1">Judul Kuis</label>
              <input 
                type="text" 
                placeholder="Misal: Kuis Rangkaian Listrik" 
                value={formTitle} 
                onChange={(e) => setFormTitle(e.target.value)} 
                className="w-full px-4 py-2 border rounded-lg" 
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-slate-800 mb-4">Pertanyaan & Pilihan Jawaban</h3>
              {formQuestions.map((q, qIndex) => (
                <div key={qIndex} className="p-4 bg-slate-50 rounded-xl mb-4 border space-y-3">
                  <input 
                    type="text" 
                    placeholder={`Tuliskan pertanyaan nomor ${qIndex + 1}...`} 
                    value={q.question} 
                    onChange={(e) => { 
                      const updated = [...formQuestions]; 
                      updated[qIndex].question = e.target.value; 
                      setFormQuestions(updated); 
                    }} 
                    className="w-full px-3 py-2 border rounded-md" 
                  />
                  
                  <label className="flex items-center gap-2 text-xs text-indigo-600 cursor-pointer font-medium">
                    <ImageIcon size={16} /> Sisipkan Gambar (Opsional)
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, qIndex)} className="hidden" />
                  </label>
                  {q.image && <img src={q.image} alt="Preview" className="h-20 rounded border" />}

                  <div className="grid grid-cols-2 gap-2">
                    {q.options.map((opt, optIndex) => (
                      <div key={optIndex} className="flex items-center gap-2">
                        <span className="font-bold text-slate-500 text-sm">{optionLabels[optIndex]}.</span>
                        <input 
                          type="text" 
                          value={opt} 
                          onChange={(e) => { 
                            const updated = [...formQuestions]; 
                            updated[qIndex].options[optIndex] = e.target.value; 
                            setFormQuestions(updated); 
                          }} 
                          className="w-full px-3 py-1 border rounded-md text-sm" 
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-4">
                    <div>
                      <label className="text-xs text-slate-600 mr-2">Jawaban Benar:</label>
                      <select
                        value={q.correct}
                        onChange={(e) => {
                          const updated = [...formQuestions];
                          updated[qIndex].correct = parseInt(e.target.value);
                          setFormQuestions(updated);
                        }}
                        className="text-sm p-1 border rounded"
                      >
                        <option value={0}>Pilihan A</option>
                        <option value={1}>Pilihan B</option>
                        <option value={2}>Pilihan C</option>
                        <option value={3}>Pilihan D</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 mr-2">Waktu Menjawab (detik):</label>
                      <input
                        type="number"
                        min={5}
                        max={300}
                        value={q.timeLimit ?? 20}
                        onChange={(e) => {
                          const updated = [...formQuestions];
                          updated[qIndex].timeLimit = parseInt(e.target.value) || 20;
                          setFormQuestions(updated);
                        }}
                        className="text-sm p-1 border rounded w-20"
                      />
                    </div>
                    <span className="text-xs text-slate-400">Skor: 15-25 poin jika benar (tercepat dapat 25), 0 jika salah</span>
                  </div>
                </div>
              ))}

              <button 
                type="button" 
                onClick={() => setFormQuestions([...formQuestions, { question: '', image: '', options: ['', '', '', ''], correct: 0, timeLimit: 20 }])}
                className="flex items-center gap-2 text-indigo-600 font-semibold text-sm mb-6"
              >
                <Plus size={16} /> Tambah Pertanyaan Lain
              </button>
            </div>

            {/* TOMBOL UTAMA BEBAS FORM WRAPPER */}
            <button 
              type="button" 
              disabled={isSaving}
              onClick={executeSaveQuiz} 
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 rounded-xl shadow-lg active:scale-95 transition flex items-center justify-center gap-2 cursor-pointer"
            >
              {isSaving ? 'Menyimpan...' : (editingCode ? 'Simpan Perubahan' : 'Simpan Ke Database Firebase')}
            </button>
          </div>
        </div>
      )}

      {/* HASIL AKHIR */}
      {view === 'result' && (
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
          <CheckCircle className="mx-auto text-emerald-500 mb-4" size={64} />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Kuis Telah Selesai!</h2>
          <p className="text-slate-500 mb-6">Terima kasih telah berpartisipasi, <span className="font-semibold">{studentName}</span>.</p>
          <div className="bg-slate-50 p-4 rounded-xl mb-6 border">
            <span className="text-sm text-slate-500 block">Skor Akhir Anda</span>
            <span className="text-4xl font-extrabold text-emerald-600">{liveQuizData?.results?.[myStudentKey]?.score ?? myScore}</span>
          </div>
          <button onClick={resetToHome} className="w-full bg-slate-800 text-white font-semibold py-3 rounded-xl">Kembali ke Menu Utama</button>
        </div>
      )}

    </div>
  );
}